import { safeNumber, lineTotal, normalizeQuoteItems, optionalQuoteNumber, quotePriceError } from "@/lib/quoteNumbers";
// Shared quote state/save engine — SINGLE SOURCE OF TRUTH for both the
// standard QuoteWorkspace and the full-screen QuoteItemWorkspace grid.
// Do not duplicate this logic elsewhere; both views must read/write the
// exact same quote document via the exact same endpoints.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";

export const nzd = (n) => new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD" }).format(safeNumber(n));
export const GST = 0.15;
const todayISO = () => new Date().toISOString().slice(0, 10);
const plusDays = (iso, d) => { const x = new Date(`${iso}T00:00:00`); x.setDate(x.getDate() + d); return x.toISOString().slice(0, 10); };

// Customer-facing display settings intentionally start EMPTY so each quote
// inherits the organisation's Quote Branding defaults until it overrides them.
export const DEFAULT_SETTINGS = {};

export const newItem = (extra = {}) => ({
  description: "", detail: "", qty: 1, unit: "ea", unit_price: 0, discount_pct: 0,
  is_section: false, cost_code: "", category: "", labour_cost: null, material_cost: null,
  plant_cost: null, internal_note: "", ...extra,
});
export const newSection = (name = "New section") => ({ ...newItem(), description: name, is_section: true, collapsed: false });

export const isNewDraft = (q) => q.status === "draft" && !(q.client_name || "").trim() && !(q.line_items || []).length;

export const STATUS_STYLE = {
  draft: "bg-zinc-200 text-zinc-700 dark:bg-white/10 dark:text-zinc-300",
  sent: "bg-sky-100 text-sky-800 dark:bg-sky-500/15 dark:text-sky-300",
  viewed: "bg-indigo-100 text-indigo-800 dark:bg-indigo-500/15 dark:text-indigo-300",
  accepted: "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300",
  declined: "bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300",
  expired: "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300",
  converted: "bg-violet-100 text-violet-800 dark:bg-violet-500/15 dark:text-violet-300",
};

export function useQuoteEditor(qid) {
  const navigate = useNavigate();
  const isNew = !qid || qid === "new";

  const [q, setQ] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saveState, setSaveState] = useState("saved"); // saved | dirty | saving
  const [clients, setClients] = useState([]);
  const [jobs, setJobs] = useState([]);
  const skipSave = useRef(true);
  const saveTimer = useRef(null);
  const dragRef = useRef(null);

  // ---------- load / create ----------
  useEffect(() => {
    (async () => {
      try {
        const [{ data: cs }, { data: js }] = await Promise.all([api.get("/clients"), api.get("/jobs")]);
        setClients(cs || []); setJobs(js || []);
        if (isNew) {
          // Explicit CRM quick-create only; canonical quote billing fields stay authoritative.
          const crmClientId = new URLSearchParams(window.location.search).get('crm_client_id');
          const crmCustomer = crmClientId ? (await api.get(`/customer-workspace/customers/${encodeURIComponent(crmClientId)}`, { __flSkipReadCache: true })).data.customer : null;
          const jobId = new URLSearchParams(window.location.search).get("job_id") || null;
          const {data: templateOptions} = await api.get("/document-templates", {__flSkipReadCache:true});
          const templateId = js.find(job => job.id === jobId)?.quote_template_id || templateOptions.defaults?.quote;
          const template = templateOptions.templates.find(t => t.id === templateId);
          const { data } = await api.post("/quotes", {
            client_name: "", ...(crmCustomer ? { client_id: crmCustomer.id, client_name: crmCustomer.name, client_email: crmCustomer.email, client_address: crmCustomer.address } : {}), job_id: jobId, issue_date: todayISO(), valid_until: plusDays(todayISO(), template?.config.validity_days || 30),
            payment_terms: template?.config.payment_terms || "20th of following month",
            terms: template?.config.terms || `This quote is valid for ${template?.config.validity_days || 30} days from the issue date. All prices are in NZD and exclude GST unless otherwise stated. Payment is due within the agreed payment terms.`,
          });
          skipSave.current = true;
          setQ({ ...data, settings: { ...DEFAULT_SETTINGS, ...(data.settings || {}) } });
          navigate(`/app/quotes/${data.id}`, { replace: true });
        } else {
          const { data } = await api.get(`/quotes/${qid}`);
          skipSave.current = true;
          setQ({ ...data, settings: { ...DEFAULT_SETTINGS, ...(data.settings || {}) } });
        }
      } catch (e) {
        toast.error(e.response?.data?.detail || "Could not load quote");
        navigate("/app/quotes");
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qid]);

  // ---------- derived totals (live, before autosave round-trip) ----------
  const totals = useMemo(() => {
    if (!q) return { subtotal: 0, discount: 0, net: 0, gst: 0, total: 0 };
    const items = (q.line_items || []).filter((i) => !i.is_section);
    const subtotal = items.reduce((s, it) => s + lineTotal(it), 0);
    let disc = 0;
    if (q.discount_type === "percentage" && q.discount_value) disc = subtotal * Math.min(100, Math.max(0, safeNumber(q.discount_value))) / 100;
    if (q.discount_type === "fixed" && q.discount_value) disc = Math.min(safeNumber(q.discount_value), subtotal);
    const net = subtotal - disc;
    let gst, total, exGst;
    if (q.gst_enabled === false) { exGst = net; gst = 0; total = net; }
    else if (q.gst_inclusive) { total = net; exGst = net / (1 + GST); gst = total - exGst; }
    else { exGst = net; gst = net * GST; total = net + gst; }
    return { subtotal, discount: disc, net: exGst, gst, total };
  }, [q]);

  // ---------- autosave ----------
  const patch = useCallback((changes) => setQ((prev) => ({ ...prev, ...changes })), []);
  useEffect(() => {
    if (!q?.id) return;
    if (skipSave.current) { skipSave.current = false; return; }
    setSaveState("dirty");
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSaveState("saving");
      try {
        const body = {
          client_id: q.client_id, client_name: q.client_name || "", client_email: q.client_email,
          client_address: q.client_address, issue_date: q.issue_date, valid_until: q.valid_until,
          job_id: q.job_id, line_items: normalizeQuoteItems(q.line_items),
          notes: q.notes, gst_inclusive: !!q.gst_inclusive, gst_enabled: q.gst_enabled !== false, title: q.title, reference: q.reference,
          description: q.description, assumptions: q.assumptions, exclusions: q.exclusions,
          payment_terms: q.payment_terms, terms: q.terms, settings: q.settings, template_id: q.template_id,
          accent_color: q.accent_color, discount_type: q.discount_type,
          discount_value: optionalQuoteNumber(q.discount_value),
          discount_reason: q.discount_reason, attachments: q.attachments || [],
          advanced: q.advanced || {}, auto_create_job: !!q.auto_create_job,
        };
        const { data } = await api.patch(`/quotes/${q.id}`, body);
        skipSave.current = true;
        setQ((prev) => ({ ...prev, ...(prev.template_id === body.template_id && prev.job_id === body.job_id ? {template_id:data.template_id, template_name:data.template_name, template_source:data.template_source} : {}), subtotal: data.subtotal, discount_amount: data.discount_amount, gst: data.gst, total: data.total, updated_at: data.updated_at }));
        setSaveState("saved");
      } catch { setSaveState("dirty"); }
    }, 1200);
    return () => clearTimeout(saveTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const saveNow = async (extra = {}) => {
    clearTimeout(saveTimer.current);
    setSaveState("saving");
    try {
      const { data } = await api.patch(`/quotes/${q.id}`, {
        client_name: q.client_name || "", line_items: normalizeQuoteItems(q.line_items), notes: q.notes,
        gst_inclusive: !!q.gst_inclusive, title: q.title, reference: q.reference,
        description: q.description, assumptions: q.assumptions, exclusions: q.exclusions,
        payment_terms: q.payment_terms, terms: q.terms, settings: q.settings, template_id: q.template_id,
        accent_color: q.accent_color, discount_type: q.discount_type, discount_value: optionalQuoteNumber(q.discount_value),
        discount_reason: q.discount_reason, attachments: q.attachments || [], advanced: q.advanced || {},
        auto_create_job: !!q.auto_create_job, job_id: q.job_id, client_id: q.client_id,
        client_email: q.client_email, client_address: q.client_address,
        issue_date: q.issue_date, valid_until: q.valid_until, ...extra,
      });
      skipSave.current = true;
      setQ({ ...data, settings: { ...DEFAULT_SETTINGS, ...(data.settings || {}) } });
      setSaveState("saved");
      return data;
    } catch (e) {
      setSaveState("dirty");
      toast.error(e.response?.data?.detail || "Save failed");
      return null;
    }
  };

  // ---------- line item ops ----------
  const items = q?.line_items || [];
  const setItems = (next) => patch({ line_items: next });
  const updateItem = (i, changes) => setItems(items.map((it, x) => (x === i ? { ...it, ...changes } : it)));
  const removeItem = (i) => {
    const it = items[i];
    if (it.is_section) {
      let end = i + 1;
      while (end < items.length && !items[end].is_section) end++;
      if (!window.confirm(`Delete section "${it.description}" and its ${end - i - 1} item(s)?`)) return;
      setItems([...items.slice(0, i), ...items.slice(end)]);
    } else setItems(items.filter((_, x) => x !== i));
  };
  const duplicateItem = (i) => setItems([...items.slice(0, i + 1), { ...items[i] }, ...items.slice(i + 1)]);
  const moveItem = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = items.slice();
    [next[i], next[j]] = [next[j], next[i]];
    setItems(next);
  };
  const addItem = (extra = {}) => { setItems([...items, newItem(extra)]); return items.length; };
  const addSection = () => setItems([...items, newSection(`Section ${items.filter((i) => i.is_section).length + 1}`)]);

  // drag & drop: move a row (or a whole section block) to a new index
  const blockRange = (i) => {
    if (!items[i]?.is_section) return [i, i + 1];
    let end = i + 1;
    while (end < items.length && !items[end].is_section) end++;
    return [i, end];
  };
  const onDrop = (targetIdx) => {
    const from = dragRef.current;
    dragRef.current = null;
    if (from == null || from === targetIdx) return;
    const [s, e] = blockRange(from);
    if (targetIdx > s && targetIdx < e) return;
    const block = items.slice(s, e);
    const rest = [...items.slice(0, s), ...items.slice(e)];
    let insert = targetIdx > s ? targetIdx - (e - s) : targetIdx;
    insert = Math.max(0, Math.min(rest.length, insert));
    setItems([...rest.slice(0, insert), ...block, ...rest.slice(insert)]);
  };

  // section subtotals: map section idx -> subtotal
  const sectionSubtotals = useMemo(() => {
    const map = {};
    let cur = null;
    items.forEach((it, i) => {
      if (it.is_section) { cur = i; map[i] = 0; }
      else if (cur != null) map[cur] += lineTotal(it);
    });
    return map;
  }, [items]);

  // ---------- actions ----------
  const openPdf = async (print = false) => {
    const error = quotePriceError(q.line_items);
    if (error) { toast.error(error); return; }
    if (!await saveNow()) return;
    try {
      const res = await api.get(`/quotes/${q.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const w = window.open(url, "_blank");
      if (print && w) w.addEventListener("load", () => w.print());
    } catch { toast.error("PDF failed"); }
  };
  const downloadPdf = async () => {
    const error = quotePriceError(q.line_items);
    if (error) { toast.error(error); return; }
    if (!await saveNow()) return;
    try {
      const res = await api.get(`/quotes/${q.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${q.quote_number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("PDF failed"); }
  };
  const uploadFiles = async (files) => {
    for (const f of files) {
      if (f.size > 15 * 1024 * 1024) { toast.error(`${f.name} is over 15MB`); continue; }
      const fd = new FormData();
      fd.append("file", f); fd.append("category", "quote_attachments");
      try {
        const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setQ((prev) => ({ ...prev, attachments: [...(prev.attachments || []), { name: f.name, path: data.storage_path, size: f.size }] }));
      } catch { toast.error(`Upload failed: ${f.name}`); }
    }
  };
  const convertToInvoice = async () => {
    try {
      const { data } = await api.post(`/quotes/${q.id}/convert-to-invoice`);
      toast.success(`Invoice ${data.invoice_number} created`);
      navigate("/app/customer-invoices");
    } catch (e) { toast.error(e.response?.data?.detail || "Conversion failed"); }
  };
  const convertToJob = async () => {
    try {
      const { data } = await api.post(`/quotes/${q.id}/convert-to-job`);
      toast.success(`Job ${data.job?.job_number} created`);
      if (data.job?.id) navigate(`/app/jobs/${data.job.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Conversion failed"); }
  };
  const respond = async (action, reason) => {
    try {
      const { data } = await api.post(`/quotes/${q.id}/respond`, { action, reason });
      skipSave.current = true;
      setQ({ ...data, settings: { ...DEFAULT_SETTINGS, ...(data.settings || {}) } });
      toast.success(action === "accept" ? "Quote marked as accepted" : "Quote marked as rejected");
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
      return false;
    }
  };

  const saveAsTemplate = async () => {
    const name = window.prompt("Template name:", q.title || q.quote_number);
    if (!name) return;
    try {
      await api.post("/quote-templates", { kind: "quote", name, data: { line_items: q.line_items, terms: q.terms, payment_terms: q.payment_terms, settings: q.settings, notes: q.notes } });
      toast.success("Saved as template");
    } catch { toast.error("Template save failed"); }
  };

  const markSynced = () => { skipSave.current = true; };

  return {
    q, setQ, loading, saveState, clients, setClients, jobs, isNew, navigate,
    totals, patch, saveNow, items, setItems, updateItem, removeItem, duplicateItem, moveItem,
    addItem, addSection, dragRef, onDrop, sectionSubtotals, markSynced,
    openPdf, downloadPdf, uploadFiles, convertToInvoice, convertToJob, respond, saveAsTemplate,
  };
}
